import { useEffect, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import { Skeleton } from "@/components/ui/skeleton"
import Footer from "@/components/footer-1"
import { CompanySocialLinks } from "@/components/company-social-links"
import { formatSalary } from "@/lib/salary"
import { formatShortDate } from "@/lib/dates"
import { companyMetaParts } from "@/lib/company"
import { Chip } from "@/components/chips"
import { EntityRow } from "@/components/entity-row"

export const Route = createFileRoute("/companies/$companyId")({
  component: CompanyPage,
})

function CompanyPage() {
  const { companyId } = Route.useParams()
  const company = useQuery(api.companies.getPublic, { id: companyId })
  // Legacy bookmarked /companies/<profileId> URLs resolve to the canonical
  // company id and redirect; the public API itself is company-id only.
  const legacyRef = useQuery(
    api.companies.resolveLegacyCompanyRef,
    company === null ? { ref: companyId } : "skip",
  )
  const navigate = useNavigate()
  useEffect(() => {
    if (company === null && legacyRef) {
      void navigate({
        to: "/companies/$companyId",
        params: { companyId: legacyRef },
        replace: true,
      })
    }
  }, [company, legacyRef, navigate])
  const [tab, setTab] = useState<"jobs" | "events">("jobs")

  if (company === undefined) {
    return (
      <div className="mkt-page home-wf">
        <div className="mkt-page-shell mkt-page-shell--detail">
          <Skeleton className="h-48 w-full" />
        </div>
        <Footer />
      </div>
    )
  }
  if (!company) {
    return (
      <div className="mkt-page home-wf">
        <div className="mkt-page-shell mkt-page-shell--narrow">
          <p>Company not found.</p>
          <Link to="/jobs/browse" className="home-wf-btn home-wf-btn--outline mt-4 inline-flex">
            Browse jobs
          </Link>
        </div>
        <Footer />
      </div>
    )
  }

  const meta = companyMetaParts({
    location: company.companyLocation,
    size: company.companySize,
    stage: company.companyStage,
  })

  return (
    <div className="mkt-page home-wf">
      <div className="mkt-page-shell mkt-page-shell--detail">
        <Link
          to="/jobs/browse"
          className="mb-6 inline-block text-sm font-bold text-[color:var(--wf-mute)] underline-offset-4 hover:text-[color:var(--wf-ink)] hover:underline"
        >
          ← All roles
        </Link>

        <div className="flex flex-wrap items-start gap-5">
          <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-[0.75rem] bg-[rgb(196_240_75/0.55)] text-2xl font-extrabold text-[color:var(--wf-ink)]">
            {company.logoFileUrl ? (
              <img
                src={company.logoFileUrl}
                alt=""
                className="size-full object-cover"
              />
            ) : (
              company.companyName.charAt(0).toUpperCase()
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="mkt-page-title">{company.companyName}</h1>
              <span className="mkt-chip">Verified</span>
            </div>
            {company.companyOneLiner && (
              <p className="mkt-page-sub mt-1">{company.companyOneLiner}</p>
            )}
            {meta.length > 0 && (
              <p className="mt-1 text-sm text-[color:var(--wf-mute)]">
                {meta.join(" · ")}
              </p>
            )}
            <CompanySocialLinks company={company} className="mt-3" />
          </div>
        </div>

        {company.companyAbout && (
          <section className="mt-8">
            <h2 className="mb-2 text-lg font-extrabold tracking-tight">About</h2>
            <p className="max-w-2xl whitespace-pre-wrap text-sm leading-relaxed text-[color:var(--wf-ink)]">
              {company.companyAbout}
            </p>
          </section>
        )}

        <section className="mt-8">
          <div
            className="mkt-rail-tabs max-w-xs"
            role="tablist"
            aria-label="Company listings"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === "jobs"}
              className={tab === "jobs" ? "mkt-rail-tab mkt-rail-tab--active" : "mkt-rail-tab"}
              onClick={() => setTab("jobs")}
            >
              Jobs{company.jobs.length > 0 ? ` · ${company.jobs.length}` : ""}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "events"}
              className={
                tab === "events" ? "mkt-rail-tab mkt-rail-tab--active" : "mkt-rail-tab"
              }
              onClick={() => setTab("events")}
            >
              Events
              {company.hackathons.length > 0 ? ` · ${company.hackathons.length}` : ""}
            </button>
          </div>

          {tab === "jobs" ? (
            <div role="tabpanel" className="mt-4">
              {company.jobs.length === 0 ? (
                <p className="text-sm text-[color:var(--wf-mute)]">
                  No open roles right now.
                </p>
              ) : (
                <div>
                  {company.jobs.map((job) => {
                    const salary = formatSalary(job)
                    return (
                      <EntityRow
                        key={job._id}
                        to="/jobs/$jobId"
                        params={{ jobId: job._id }}
                        title={job.title}
                        chips={
                          <>
                            <Chip emphasis>{job.employmentType}</Chip>
                            {salary && <Chip>{salary}</Chip>}
                          </>
                        }
                        meta={job.location}
                        tags={{ items: job.skills, max: 4 }}
                        cue="View role →"
                      />
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            <div role="tabpanel" className="mt-4">
              {company.hackathons.length === 0 ? (
                <p className="text-sm text-[color:var(--wf-mute)]">
                  No upcoming events right now.
                </p>
              ) : (
                <div>
                  {company.hackathons.map((h) => {
                    const where = [h.venue, h.city].filter(Boolean).join(", ")
                    return (
                      <EntityRow
                        key={h._id}
                        to="/hackathons/$hackathonId"
                        params={{ hackathonId: h._id }}
                        title={h.title}
                        chips={<Chip emphasis>{formatShortDate(h.startsAt)}</Chip>}
                        meta={where || undefined}
                        cue="View event →"
                      />
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  )
}
